import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { usePode } from '@/api/me';
import { BarraDeAcoesLista, usePaginarLista } from '@/components/barra-lista';
import { ErroLista } from '@/components/cargos';
import { BotaoRelatorios } from '@/relatorios';
import { useSessao } from '@/lib/session';
import { formatarQuantidade } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CondicaoDetalhe, TEXTO_DIVERGENCIA } from './condicao-detalhe';
import { TIPO_ROTULO } from './condicao-form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SeloProtheus } from '@/components/integracoes-erp/selo-protheus';

export interface BuscaCondicoes {
  id?: string;
}

/** `validateSearch`: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBuscaCondicoes(bruto: Record<string, unknown>): BuscaCondicoes {
  const id = typeof bruto.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(bruto.id) ? bruto.id : undefined;
  return id ? { id } : {};
}

/** Tela "Condições de pagamento" (/cadastros/condicoes): lista; registro abre por `?id=` (`novo` cria). */
export function CondicoesPage({ busca }: { busca: BuscaCondicoes }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const { pode } = usePode();
  const podeEditar = pode('tabela.editar');
  const [aviso, setAviso] = useState<string | null>(null);
  const lista = useCondicoesPagamento({ limit: 200 });
  const itens = lista.data ?? [];
  const pagItens = usePaginarLista(itens);
  /** Coluna de prazo medio so aparece se a API mandar na lista (hoje so vem no detalhe). */
  const mostraPrazo = itens.some((c) => c.prazoMedio !== undefined);

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver as condições.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroLista erro={lista.error} onTentar={() => void lista.refetch()} titulo="Não foi possível carregar as condições" /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando condições…</Mensagem>;
  else if (itens.length === 0) {
    corpo = (
      <Mensagem papel="status">
        Nenhuma condição de pagamento cadastrada.
        {podeEditar && (
          <>
            {' '}
            <Link to="/cadastros/condicoes" search={{ id: 'novo' }} className="font-medium text-primary hover:underline">
              Cadastre a primeira
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <TooltipProvider>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Lista de condições de pagamento">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="px-4 py-2.5 font-medium">Identificador</th>
                <th scope="col" className="px-3 py-2.5 font-medium">Nome</th>
                <th scope="col" className="px-3 py-2.5 font-medium">Tipo</th>
                <th scope="col" className="px-3 py-2.5 font-medium">Parcelas</th>
                {mostraPrazo && <th scope="col" className="px-3 py-2.5 font-medium">Prazo médio</th>}
                <th scope="col" className="px-4 py-2.5 font-medium">Situação</th>
              </tr>
            </thead>
            <tbody>
              {pagItens.visiveis.map((c) => (
                <tr
                  key={c.id}
                  data-testid="linha-condicao"
                  onClick={() => void navigate({ to: '/cadastros/condicoes', search: { id: c.id } })}
                  className={cn('cursor-pointer border-t border-line-soft hover:bg-surface-muted', busca.id === c.id && 'bg-line-soft')}
                >
                  <td className="px-4 py-3 font-semibold">{c.codigo}</td>
                  <td className="px-3 py-3">
                    <span className="flex flex-wrap items-center gap-2">
                      {c.nome}
                      {c.doProtheus && <SeloProtheus />}
                      {c.prazosDivergentes && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} data-testid="chip-divergentes" className="rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink">
                              prazos divergentes do padrão
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{TEXTO_DIVERGENCIA}</TooltipContent>
                        </Tooltip>
                      )}
                      {c.versaoAnteriorId && (
                        <span className="rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body" data-testid="chip-versao">
                          {`versão de ${itens.find((x) => x.id === c.versaoAnteriorId)?.codigo ?? 'condição anterior'}`}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-ink-secondary">{c.tipo ? TIPO_ROTULO[c.tipo] : 'Importada'}</td>
                  <td className="tabular px-3 py-3 text-ink-secondary">{c.quantidade !== null ? formatarQuantidade(String(c.quantidade)) : '—'}</td>
                  {mostraPrazo && <td className="tabular px-3 py-3 text-ink-secondary">{c.prazoMedio !== undefined ? `${formatarQuantidade(c.prazoMedio)} dias` : '—'}</td>}
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-caption font-semibold', c.ativa ? 'bg-success-bg text-success-ink' : 'bg-line-soft text-ink-body')}>
                      {c.ativa ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Cadastros <span aria-hidden="true">/</span> Condições de pagamento
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Condições de pagamento</h1>
        </div>
      </div>

      <section aria-label="Lista de condições" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        {corpo}
      </section>

      {busca.id === 'novo' && !podeEditar ? (
        <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-body" role="status">
          Seu acesso não permite cadastrar condições de pagamento.
        </section>
      ) : (
        busca.id && (
          <>
            {aviso && <p role="status" className="rounded-xl border border-border bg-card px-4 py-3 text-small text-ink-body">{aviso}</p>}
            <TooltipProvider>
              <CondicaoDetalhe key={busca.id} id={busca.id === 'novo' ? undefined : busca.id} podeEditar={podeEditar} lista={itens} onAviso={setAviso} />
            </TooltipProvider>
          </>
        )
      )}
      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Nova condição', onClick: () => void navigate({ to: '/cadastros/condicoes', search: { id: 'novo' } }) } : undefined}
        paginacao={lista.data ? pagItens.paginacao : undefined}
        relatorios={<BotaoRelatorios tela="condicoes" />}
      />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
